import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _reverse from 'lodash/reverse';
import { getUserInfo } from 'Utils';
import * as analytics from 'Utils/analytics';
import {
  deleteInterest,
  filterInterests,
  getInterests,
  programmingInterests,
  saveInterest,
  setOptout,
} from 'Services/interests';
import InterestsSelector from 'Components/profile/InterestsSelector';
import Recommendations from 'Components/profile/Recommendations';

const { EVENTS } = analytics;

const Interests = ({
  country,
  dispatchToast,
  isMobile,
  language,
  strings,
  uim,
}) => {
  const [hasInterests, setHasInterests] = useState(false);
  const [rails, setRails] = useState([]);
  const [selections, setSelections] = useState([]);
  const [userInterests, setUserInterests] = useState([]);

  const deselectInterest = async (item) => {
    analytics.event({ action: EVENTS.deselectInterestItem, label: item });

    const items = selections.filter((selections) => selections !== item);
    setSelections(items);

    try {
      const { userToken, userId } = await getUserInfo(uim);
      const deleted = await deleteInterest(item, userId, userToken);
      if (deleted) refresh(items);
      if (!items.length) {
        try {
          setOptout(true, userId, userToken);
        } catch ({ message }) {
          console.error(`Error opting out of interests: ${message}`);
        }
      }
    } catch ({ message }) {
      console.error(`Error deleting interest '${item}': ${message}`);
      dispatchToast({
        message: strings.couldNotRemove,
      });
      setSelections((selections) => {
        selections.splice(selections.length, 0, item);
        return selections;
      });
    }
  };

  const selectInterest = async (item) => {
    analytics.event({ action: EVENTS.selectInterestItem, label: item });

    const items = [...selections, item];
    setSelections(items);

    try {
      const { userToken, userId } = await getUserInfo(uim);
      const saved = await saveInterest(item, userId, userToken);
      if (saved) {
        const interests = await getInterests(userId, userToken);
        if (interests) refresh(items);
      }
    } catch ({ message }) {
      console.error(`Error adding interest '${item}': ${message}`);
      dispatchToast({
        message: strings.errorMessage,
      });
      const items = selections.filter((selections) => selections !== item);
      setSelections(items);
    }
  };

  const onChange = (event) => {
    if (event.target.checked) {
      selectInterest(event.target.value);
    } else {
      deselectInterest(event.target.value);
    }
  };

  const refresh = (items) => {
    setUserInterests(items);
    if (items?.length) {
      setHasInterests(true);
    } else {
      setHasInterests(false);
    }
  };

  const formatInterests = async () => {
    const { userToken, userId } = await getUserInfo(uim);
    const interests = await getInterests(userId, userToken);
    if (interests) {
      setSelections(interests);
      setUserInterests(interests);
      if (interests?.length) {
        setHasInterests(true);
      } else {
        setHasInterests(false);
      }
    }
  };

  const getRails = () => {
    setRails(_reverse([...userInterests]));
  };

  useEffect(() => {
    formatInterests();
  }, []);

  useEffect(() => {
    getRails();
  }, [userInterests]);

  return (
    <div className="interests">
      <div className="sidebar-view__header desktop__header">
        <h1 className="sidebar-view__heading">{strings.interests}</h1>
      </div>
      <div className="sidebar-view__inner">
        <div className="row__label">{strings.interestsHeading2}</div>
        <InterestsSelector
          interests={filterInterests('programming')}
          interestsArray={programmingInterests}
          onChange={onChange}
          selections={selections}
          userInterests={userInterests}
        />
        {!isMobile && (
          <>
            {!hasInterests ? (
              <>
                <div className="row__label">
                  {strings.interestsRecommendationsHeading}
                </div>
                <div className="row__content">
                  {strings.interestsRecommendationsEmpty}
                </div>
              </>
            ) : (
              rails.map((rail) => (
                <Recommendations
                  country={country}
                  favoritesType="MIXED"
                  interestsBased={true}
                  isVideo={false}
                  key={rail}
                  language={language}
                  strings={strings}
                  type={rail}
                  uim={uim}
                  verticalOnDesktop={false}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

Interests.propTypes = {
  applicationConfig: PropTypes.object.isRequired,
  country: PropTypes.string.isRequired,
  dispatchToast: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  uim: PropTypes.object.isRequired,
};

Interests.defaultProps = {};

export default Interests;
